import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  Button,
  Box,
  Menu,
  MenuItem,
  Typography,
  Select,
  InputLabel,
  FormControl,
  Paper,
  List,
  ListItemText,
} from "@mui/material";
import { useNavigate, useLocation, useParams, Link } from "react-router-dom";
import axios from "axios";
import { IoIosArrowBack } from "react-icons/io";

const CustomColumnPerformance = () => {
  const [anchorEl, setAnchorEl] = useState(null); // For main dropdown
  const [subAnchorEl, setSubAnchorEl] = useState(null); // For sub-options dropdown
  const [selectedCategory, setSelectedCategory] = useState(null); // Selected main category
  const [manualFormula, setManualFormula] = useState(""); // Formula input
  const [columnName, setColumnName] = useState("");
  const [description, setDescription] = useState("");
  const [dataFormat, setDataFormat] = useState("Number (123)"); // Default selected option
  const [isEditMode, setIsEditMode] = useState(false);

  // States for autocompletion
  const [suggestions, setSuggestions] = useState([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const textareaRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // Get the id from the URL

  // Column categories with options
  const columnCategories = {
    Performance: [
      { name: "Clicks", key: "clicks" },
      { name: "Impr.", key: "impressions" },
      { name: "Avg. CPC", key: "average_cpc" },
      { name: "Costs", key: "costs" },
      { name: "CTR", key: "ctr" },
      { name: "Interaction Rate", key: "interaction_rate" },
      { name: "Interaction", key: "interactions" },
    ],
    Conversions: [
      { name: "Conversions", key: "conversions" },
      { name: "Cost / conv", key: "cost_per_conv" },
      { name: "Conv. Rate", key: "conversion_rate" },
    ],
  };

  // Flattened metrics list for search queries
  const allMetrics = Object.values(columnCategories).flat();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const colId = searchParams.get("id");

    if (colId) {
      setIsEditMode(true);
      const email = localStorage.getItem("email");

      axios
        .post(
          "https://api.confidanto.com/custom-columns-project-performance/get",
          { email },
        )
        .then((response) => {
          const columnToEdit = response.data.data.find(
            (col) => col.id === parseInt(colId),
          );
          if (columnToEdit) {
            setColumnName(columnToEdit.custom_column);
            setDescription(columnToEdit.description);
            setManualFormula(columnToEdit.formula);
          }
        })
        .catch((error) => {
          console.error("Error fetching custom column data:", error);
        });
    }
  }, [location.search]);

  // Open main dropdown
  const handleColumnClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Close all menus
  const handleClose = () => {
    setAnchorEl(null);
    setSubAnchorEl(null);
    setSelectedCategory(null);
  };

  // Handle main category selection
  const handleCategorySelect = (event, category) => {
    setSelectedCategory(category);
    setSubAnchorEl(event.currentTarget); // Open the sub-options dropdown
  };

  // Add selected option to formula
  const handleOptionSelect = (columnKey) => {
    setManualFormula((prev) => prev + columnKey);
    setSubAnchorEl(null);
    setSelectedCategory(null);
    handleClose();
  };

  // Handle auto-suggest selection logic
  const handleSuggestSelect = (metricKey) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const selectionStart = textarea.selectionStart;
    const textBeforeCursor = manualFormula.slice(0, selectionStart);
    const textAfterCursor = manualFormula.slice(selectionStart);

    // Identify current word before the cursor
    const words = textBeforeCursor.split(/[^a-zA-Z0-9_]/);
    const activeWord = words[words.length - 1] || "";

    // Replace only the typed partial word with the correct key
    const newTextBeforeCursor =
      textBeforeCursor.slice(0, textBeforeCursor.length - activeWord.length) +
      metricKey;
    const newFormula = newTextBeforeCursor + textAfterCursor;

    setManualFormula(newFormula);
    setSuggestions([]);

    // Restore text focus and put the cursor right after the newly added word
    const newCursorPos = newTextBeforeCursor.length;
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Monitor text input changes and filter suggestions
  const handleTextChange = (e) => {
    const val = e.target.value;
    setManualFormula(val);

    const selectionStart = e.target.selectionStart;
    const textBeforeCursor = val.slice(0, selectionStart);
    const words = textBeforeCursor.split(/[^a-zA-Z0-9_]/);
    const activeWord = words[words.length - 1] || "";

    if (activeWord.trim().length > 0) {
      const matches = allMetrics.filter(
        (metric) =>
          metric.name.toLowerCase().includes(activeWord.toLowerCase()) ||
          metric.key.toLowerCase().includes(activeWord.toLowerCase()),
      );

      // Avoid showing dropdown if input is an exact single match already
      const isExactMatch =
        matches.length === 1 && matches[0].key === activeWord;
      if (isExactMatch) {
        setSuggestions([]);
      } else {
        setSuggestions(matches);
        setActiveSuggestionIndex(0);
      }
    } else {
      setSuggestions([]);
    }
  };

  // Keyboard navigation inside the autocomplete suggestion list
  const handleKeyDown = (e) => {
    if (suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveSuggestionIndex((prev) => (prev + 1) % suggestions.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveSuggestionIndex(
          (prev) => (prev - 1 + suggestions.length) % suggestions.length,
        );
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        handleSuggestSelect(suggestions[activeSuggestionIndex].key);
      } else if (e.key === "Escape") {
        setSuggestions([]);
      }
    }
  };

  // Validation logic
  const isFormulaValid = () => {
    const validKeys = allMetrics.map((item) => item.key);
    return validKeys.some((key) => manualFormula.includes(key));
  };

  const isSaveDisabled = () => {
    return !(columnName?.trim() && isFormulaValid());
  };

  const handleSave = async () => {
    const searchParams = new URLSearchParams(location.search);
    const colId = searchParams.get("id");

    if (isEditMode && colId) {
      const requestBody = {
        email: localStorage.getItem("email"),
        customColumnId: parseInt(colId),
        newCustomColumn: columnName,
        description: description,
        formula: manualFormula,
      };

      await axios
        .post(
          "https://api.confidanto.com/custom-columns-project-performance/edit",
          requestBody,
        )
        .then((res) => {
          alert("Custom Column Updated");
          navigate(-1);
        })
        .catch((err) => {
          alert("Custom Column not Updated");
          console.log("Error ", err);
        });
    } else {
      const requestBody = {
        formula: manualFormula,
        customColumn: columnName,
        description: description,
        email: localStorage.getItem("email"),
      };

      await axios
        .post(
          "https://api.confidanto.com/custom-columns-project-performance/create",
          requestBody,
        )
        .then((res) => {
          alert("Custom Column Added");
          navigate(-1);
        })
        .catch((err) => {
          alert("Custom Column not Added");
          console.log("Error ", err);
        });
    }
  };

  const handleCancel = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate("/google-ads/campaigns");
    }
  };

  return (
    <Box className="p-6 md:p-8 bg-white shadow-sm border border-slate-100 rounded-xl w-full max-w-5xl mx-auto my-6">
      {/* Header Section */}
      <div className="flex items-center gap-3 mb-8 pb-4 border-b border-slate-100">
        <Link
          to={`/ads-project-performance`}
          className="flex items-center justify-center h-9 w-9 rounded-lg hover:bg-slate-100 text-gray-500 transition-all border border-slate-200"
        >
          <IoIosArrowBack className="h-5 w-5" />
        </Link>
        <Typography variant="h5" className="font-semibold text-gray-800">
          {isEditMode ? "Edit Performance Column" : "Create Performance Column"}
        </Typography>
      </div>

      {/* Input Metadata Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <TextField
          fullWidth
          required
          label="Name"
          placeholder="Enter a name"
          variant="outlined"
          value={columnName}
          onChange={(e) => setColumnName(e.target.value)}
          inputProps={{ maxLength: 40 }}
          className="bg-white"
        />

        <TextField
          fullWidth
          label="Description"
          placeholder="Enter a description (optional)"
          variant="outlined"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          inputProps={{ maxLength: 180 }}
          className="bg-white"
        />

        <FormControl fullWidth>
          <InputLabel>Data Format</InputLabel>
          <Select
            value={dataFormat}
            onChange={(e) => setDataFormat(e.target.value)}
            label="Data Format"
            className="bg-white"
          >
            <MenuItem value="Number (123)">Number (123)</MenuItem>
            <MenuItem value="Percent (%)">Percent (%)</MenuItem>
            <MenuItem value="Money (₹)">Money (₹)</MenuItem>
            <MenuItem value="Text (abc)">Text (abc)</MenuItem>
            <MenuItem value="True/False">True/False</MenuItem>
          </Select>
        </FormControl>
      </div>

      {/* Formula Workspace Section */}
      <div className="border border-slate-200 bg-slate-50 rounded-xl p-5 mb-8">
        <Typography
          variant="subtitle2"
          className="text-slate-600 font-semibold mb-3"
        >
          Formula Builder
        </Typography>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 pb-4 mb-4 border-b border-slate-200">
          <Button
            variant="outlined"
            onClick={handleColumnClick}
            className="border-blue-500 text-blue-600 hover:bg-blue-50 font-semibold py-1 px-4 normal-case"
          >
            + Add Column Metric
          </Button>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {Object.keys(columnCategories).map((category) => (
              <MenuItem
                key={category}
                onClick={(event) => handleCategorySelect(event, category)}
              >
                {category}
              </MenuItem>
            ))}
          </Menu>

          {/* Sub-options Dropdown */}
          {selectedCategory && (
            <Menu
              anchorEl={subAnchorEl}
              open={Boolean(subAnchorEl)}
              onClose={() => setSubAnchorEl(null)}
            >
              <Typography
                variant="subtitle2"
                className="px-4 py-2 text-gray-500 font-semibold"
              >
                {selectedCategory}
              </Typography>
              {columnCategories[selectedCategory].map((option) => (
                <MenuItem
                  key={option.key}
                  onClick={() => handleOptionSelect(option.key)}
                >
                  {option.name}
                </MenuItem>
              ))}
            </Menu>
          )}

          {/* Divider */}
          <div className="h-6 w-px bg-slate-300 mx-2 hidden sm:block" />

          {/* Operators Group */}
          <div className="flex items-center gap-1.5">
            {["+", "-", "*", "/", "%", "(", ")"].map((symbol) => (
              <button
                key={symbol}
                type="button"
                onClick={() => setManualFormula((prev) => prev + symbol)}
                className="h-8 w-8 flex items-center justify-center text-sm font-semibold bg-white hover:bg-slate-200 border border-slate-200 rounded text-slate-700 transition-colors"
              >
                {symbol}
              </button>
            ))}
          </div>
        </div>

        {/* Suggestion & Text Area Input container */}
        <div className="relative">
          <TextField
            fullWidth
            multiline
            rows={6}
            variant="outlined"
            value={manualFormula}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder="e.g., clicks / impressions"
            className="bg-white"
            inputRef={textareaRef}
            InputProps={{
              classes: {
                input:
                  "font-mono text-sm tracking-wide text-slate-800 leading-relaxed",
              },
            }}
          />

          {/* Floating Auto-suggest List */}
          {suggestions.length > 0 && (
            <Paper className="absolute z-50 left-2 right-2 bottom-full mb-1 max-h-48 overflow-y-auto border border-slate-200 shadow-xl rounded-lg bg-white">
              <List dense className="py-1">
                {suggestions.map((item, idx) => (
                  <MenuItem
                    key={item.key}
                    selected={idx === activeSuggestionIndex}
                    onClick={() => handleSuggestSelect(item.key)}
                    className="flex justify-between items-center py-2 px-4"
                  >
                    <ListItemText
                      primary={item.name}
                      secondary={item.key}
                      primaryTypographyProps={{
                        className: "text-sm font-medium",
                      }}
                      secondaryTypographyProps={{
                        className: "font-mono text-xs text-slate-400",
                      }}
                    />
                  </MenuItem>
                ))}
              </List>
            </Paper>
          )}
        </div>
        <Typography variant="caption" className="text-slate-400 mt-2 block">
          Use the toolbar above, type metric names directly to see suggestions,
          or type operators to construct your custom formula.
        </Typography>
      </div>

      {/* Action Buttons Section */}
      <Box className="flex justify-end items-center gap-3 pt-4 border-t border-slate-100">
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleCancel}
          className="text-slate-600 border-slate-300 hover:bg-slate-50 normal-case px-5"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isSaveDisabled()}
          className="bg-blue-600 hover:bg-blue-700 normal-case px-6 text-white"
        >
          {isEditMode ? "Update" : "Save"}
        </Button>
      </Box>
    </Box>
  );
};

export default CustomColumnPerformance;
